import { ApostleSkill, ApostleSkillType } from '@apeiron/apostle'
import { MayNull, NextImage, noForwardProps } from '@apeiron/temp'
import { ApeironTooltip } from '@apeiron/ui'
import { Box, styled, Typography } from '@mui/material'
import ColorizedTypography from '@src/components/share/generic/ColorizedTypography'
import SkillPlayback from '@src/components/share/skill/SkillPlayback'
import { SkillColorizeMapping } from '@src/constants/layout'
import { SkillPlaybackType } from '@src/constants/skill'
import { useSkillContext } from '@src/contexts/skill/SkillContext'
import * as R from 'ramda'
import { FC, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

const Container = styled(Box)`
  align-items: flex-start;
  color: #333;
  display: flex;
  min-height: 60px;
  padding: 8px 0px;
  position: relative;
`

const ImageContainer = styled(Box)`
  position: relative;
  margin-right: 10px;
`

const Image = styled(NextImage, noForwardProps)<SquareProps>`
  ${props =>
    props.__square
      ? `
        aspect-ratio: 1;
        transform: scale(1.15);
        width: 90px;
      `
      : `
        aspect-ratio: 417 / 607;
        width: 90px;
      `};
  object-fit: contain;
`

const Level = styled(Typography)`
  background-color: black;
  top: 96px;
  font-size: 12px;
  font-weight: 500;
  width: 25px;
  text-align: center;
  left: 14px;
  white-space: pre-line;
  position: absolute;
`

const Mana = styled(Typography)`
  color: white;
  position: absolute;
  top: 14px;
  left: 13px;
  font-size: 12px;
`

const InfoBox = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 3px;
  padding: 5px 0px;
`

const NameBox = styled(Box)`
  align-items: center;
  display: flex;
  gap: 10px;
`

const Name = styled(Typography, noForwardProps)<SkillElementProps>`
  color: #ccc;
  font-size: 16px;
  line-height: 20px;
`

const Description = styled(ColorizedTypography)`
  color: #ccc;
  font-size: 12px;
  font-weight: 300;
  line-height: 18px;
`

const ApostleSkillItem: FC<Props> = props => {
  const { className, hoverMode = false, skill } = props

  const { t } = useTranslation()

  const { showDetail, skillGlossary } = useSkillContext()

  const handleOnCheckGlossary = useCallback(
    (searchKey: string): string | null => {
      return R.propOr(null, R.toLower(searchKey), skillGlossary)
    },
    [skillGlossary]
  )

  if (R.isNil(skill)) {
    return null
  }

  const { image, level, mana, name, type, uniqueKey } = skill

  const description = showDetail
    ? R.prop('description', skill) || R.prop('descriptionShort', skill) || ''
    : R.prop('descriptionShort', skill) || R.prop('description', skill) || ''

  const isAutoAttack = type === ApostleSkillType.AutoAttack

  const isShowPlayback =
    type !== ApostleSkillType.AutoAttack && type !== ApostleSkillType.Innate

  const Info = (
    <InfoBox className='Custom-Info-Box'>
      <NameBox className='Custom-Name-Box'>
        <Name className='Custom-Name'>{name || ''}</Name>
        {isShowPlayback && (
          <SkillPlayback
            className='Custom-Skill-Playback'
            name={name}
            skill={uniqueKey}
            type={SkillPlaybackType.Apostle}
          />
        )}
      </NameBox>
      <Description
        className='Custom-Description'
        colorizeMap={SkillColorizeMapping}
        text={description}
        checkDictionary={handleOnCheckGlossary}
      />
    </InfoBox>
  )

  const hoverId = hoverMode ? uniqueKey : undefined

  const isInnate = type === ApostleSkillType.Innate

  return (
    <Container className={className} data-tooltip-id={hoverId}>
      {!isAutoAttack && (
        <ImageContainer className='Custom-Image-Container'>
          <Image __square={isInnate} className='Custom-Image' src={image} />
        </ImageContainer>
      )}
      {mana > 0 && <Mana className='Custom-Mana'>{mana}</Mana>}
      {level > 0 && <Level>{t('apostle.level_count', { count: level })}</Level>}
      {hoverMode ? <ApeironTooltip id={hoverId}>{Info}</ApeironTooltip> : Info}
    </Container>
  )
}

type SquareProps = {
  __square: boolean
}

type SkillElementProps = {
  color?: string
}

type Props = {
  className?: string
  hoverMode?: boolean
  skill: MayNull<ApostleSkill>
}

export default ApostleSkillItem
